import React from "react";
import { BarChart3, Filter, Mail, Siren, BoxIcon } from "lucide-react";
import { PixelCursor } from "../alt/PixelCursor";
import { EXPORT_BODY, EXPORT_META, EXPORT_SHADOW, EXPORT_TITLE } from "./exportUi";
/**
 * THE SKILLS LIBRARY, AS A SHELF — AN EXPORT-ONLY ASSET.
 *
 * WHY THIS AND NOT THE SCREEN. `SkillsScreen` is the real product view: four
 * rows in app chrome, sized for someone scanning a list they already
 * understand. As a downloadable asset it under-sells the idea — a list of four
 * rows says "there are four skills", when the claim is that a company builds a
 * LIBRARY of them and every agent draws on it.
 *
 * So this borrows the template wall's treatment, for the same reason it works
 * there: an oversized grid on a ruled board, dissolved into the ground by a
 * single radial mask rather than terminated by a frame. Nothing stops the
 * artwork except light falling off, so the eye assumes the shelf continues
 * past what is shown. One card sits under the light at full weight with the
 * pointer resting on it, which gives a dense grid one thing to actually look
 * at — and names WHICH skill the frame is about.
 *
 * A skill is somebody's expertise written down once, so the author is the
 * credential and the use count is the proof it gets reused. Both are on every
 * card; neither is on the marketing copy.
 *
 * Static by design, like every other frame on the export page.
 */

type Skill = {
  name: string;
  captures: string;
  icon: BoxIcon;
  group: string;
  by: string;
  initials: string;
  color: string;
  used: number;
};

/** Named as the piece of knowledge itself, not as a sentence about it — a
 * library of nouns scans, a list of sentences has to be read. Matches the
 * product screen's own set so the two assets agree. */
const SKILLS: Skill[] = [{
  name: 'Inbound lead qualification',
  captures: 'The questions we ask, the disqualifiers, and where the answer gets written.',
  icon: Filter,
  group: 'Marketing',
  by: 'Priya Nair',
  initials: 'PN',
  color: '#7C2D12',
  used: 142
}, {
  name: 'Renewal email drafting',
  captures: 'Tone, structure and the account facts every renewal note has to carry.',
  icon: Mail,
  group: 'Sales',
  by: 'Marcus Lee',
  initials: 'ML',
  color: '#134E4A',
  used: 211
}, {
  name: 'Weekly reporting format',
  captures: 'Which numbers lead, what gets compared, and how the summary is written.',
  icon: BarChart3,
  group: 'Leadership',
  by: 'Sarah Chen',
  initials: 'SC',
  color: '#1E3A5F',
  used: 96
}, {
  name: 'P1 incident escalation',
  captures: 'Who is paged, in what order, and what the first customer update says.',
  icon: Siren,
  group: 'Customer Success',
  by: 'Tom Alvarez',
  initials: 'TA',
  color: '#3F3A2E',
  used: 38
}];

/** Off-centre and high, so the light reads as a camera pointed at something
 * rather than as a vignette applied to everything. Shared by the mask and the
 * ruled board so the two fall off together. */
const SHELF_LIGHT = 'radial-gradient(118% 95% at 46% 32%, #000 0%, #000 36%, rgba(0,0,0,0.74) 57%, rgba(0,0,0,0.3) 76%, transparent 90%)';
function SkillCard({
  skill,
  className = ''
}: {
  skill: Skill;
  className?: string;
}) {
  const Icon = skill.icon;
  return <div className={`rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${className}`} data-id="element-3276" data-mp="396t3q8pi0ge4">
      <div className="flex items-center gap-2.5" data-id="element-3277" data-mp="347q0mtt2wk8s">
        {/* The ink tile matches the product screen's skill rows, so the asset
         and the real UI read as the same application. */}
        <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-3278" data-mp="pomy40xkfap">
          <Icon size={14} strokeWidth={1.9} className="text-white" data-id="element-3279" data-mp="3nv7auvty6r0h" />
        </span>
        <span className="truncate text-[12px] font-semibold text-[#8A7B6D]" data-id="element-3280" data-mp="2ggueac7e6h8x">{skill.group}</span>
      </div>

      <p className={`${EXPORT_TITLE} mt-3`} data-id="element-3281" data-mp="2ceze5b1769wq">{skill.name}</p>
      <p className={`${EXPORT_BODY} mt-1.5`} data-id="element-3282" data-mp="2chljr54j2fcb">{skill.captures}</p>

      {/* Authorship and reuse, on one line. The person is the credential; the
       count is the evidence it was worth writing down. */}
      <div className="mt-4 flex items-center gap-2 border-t border-[#F1EAE0] pt-3" data-id="element-3283" data-mp="8zlwzkk49r4m">
        <span aria-hidden="true" className="flex size-[18px] shrink-0 items-center justify-center rounded-full text-[7.5px] font-bold text-white" style={{
        backgroundColor: skill.color
      }} data-id="element-3284" data-mp="3qfu2p9uj91ug">
          {skill.initials}
        </span>
        <span className={`${EXPORT_META} truncate font-semibold text-[#2B211A]`} data-id="element-3285" data-mp="rwwyzcx4uynr">{skill.by}</span>
        <span className="ml-auto shrink-0 rounded-full bg-[#F1EAE0] px-2 py-0.5 text-[11.5px] font-bold text-[#6B5D52]" data-id="element-3286" data-mp="dwvuzolknb42">
          {skill.used} uses
        </span>
      </div>
    </div>;
}
export function SkillShelf({
  /**
   * How many cards share the shelf. Four on a wide artboard; one on a phone
   * artboard, where the same four cards at a quarter of 400px would be
   * illegible. The cards keep their own size in both — the shelf re-composes
   * rather than scaling, which is the difference between an asset that reads
   * on a vertical video and one that has been shrunk to fit.
   */
  columns = 4
}: {
  columns?: 1 | 2 | 4;
} = {}) {
  return <div aria-hidden="true" className="relative w-full select-none" style={{
    maskImage: SHELF_LIGHT,
    WebkitMaskImage: SHELF_LIGHT
  }} data-id="element-3287" data-mp="352bq9jd0pj5g">
      {/* The board the library is ruled on. No fill and no border — only the
       grid — so light is the only thing defining where it ends. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.55]" style={{
      backgroundImage: 'linear-gradient(to right, #E7DCCB 1px, transparent 1px), linear-gradient(to bottom, #E7DCCB 1px, transparent 1px)',
      backgroundSize: '72px 72px'
    }} data-id="element-3288" data-mp="27gw9gy42kk8t" />

      <div className={`relative grid gap-6 px-6 py-10 ${columns === 1 ? 'grid-cols-1' : columns === 2 ? 'grid-cols-2' : 'grid-cols-4'}`} data-id="element-3289" data-mp="1tmn3arzrkk4n">
        {SKILLS.map((skill, index) => {
        // The card under the light. On a single column the light falls near
        // the top, so the first card is the lit one rather than the second.
        const lit = columns === 1 ? index === 0 : index === 1;
        return <div key={skill.name} className="relative" data-id="element-3290" data-mp="2n6ltlgg5e57a" data-mpi={index}>
              <SkillCard skill={skill} className={lit ? EXPORT_SHADOW : 'opacity-[0.72] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_12px_28px_-18px_rgba(43,33,26,0.16)]'} data-id="element-3291" data-mp="2s4459eljuyiz" />
              {/* Half on and half off the lit card, so it reads as resting
               over the library rather than as an icon inside a card. */}
              {lit && <PixelCursor className="pointer-events-none absolute -bottom-6 -left-5 h-14 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)]" data-id="element-3292" data-mp="24drp5gg5hvhb" />}
            </div>;
      })}
      </div>
    </div>;
}